import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ShoppingBag, Check, Heart, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PreOrderModal from "@/components/PreOrderModal";
import ProductCard from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, isProductSaved, saveProduct, unsaveProduct } = useAuth();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const categoryNames = {
    "lanterns": "Lanterns",
    "candle-holders": "Candle Holders",
    "vases": "Vases",
    "christmas-tree": "Christmas Tree",
  };

  useEffect(() => {
    // Scroll to top when product changes
    window.scrollTo(0, 0);
    setSelectedImageIndex(0);
    
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);

        // Fetch related products
        const relatedRes = await axios.get(`${API}/products?category=${response.data.category}`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== productId).slice(0, 4));
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Get all images (main + additional)
  const getAllImages = () => {
    if (!product) return [];
    const images = [product.image_url];
    if (product.images && product.images.length > 0) {
      images.push(...product.images);
    }
    return images;
  };

  const allImages = getAllImages();

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const isSaved = product ? isProductSaved(product.id) : false;

  const handleSaveToggle = async () => {
    if (!isLoggedIn) {
      navigate("/login");
      toast.info("Please login to save products");
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        await unsaveProduct(product.id);
        toast.success("Removed from saved");
      } else {
        await saveProduct(product.id);
        toast.success("Added to saved!");
      }
    } catch (error) {
      toast.error("Failed to update saved products");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square skeleton rounded-2xl" />
            <div className="space-y-6">
              <div className="h-8 skeleton rounded w-3/4" />
              <div className="h-6 skeleton rounded w-1/4" />
              <div className="h-32 skeleton rounded w-full" />
              <div className="h-14 skeleton rounded-full w-48" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 text-center">
          <h1 className="font-heading text-2xl text-foreground">Product not found</h1>
          <Link to="/" className="text-primary font-medium mt-4 inline-block hover:underline">
            Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const features = [
    product.material && { label: "Material", value: product.material },
    product.dimensions && { label: "Dimensions", value: product.dimensions },
    product.weight && { label: "Weight", value: product.weight },
  ].filter(Boolean);

  const generalFeatures = [
    "Premium quality materials",
    "Handcrafted with care",
    "Perfect for gifting",
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="product-page">
      <Header />

      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/category/${product.category}`} className="hover:text-foreground transition-colors">
              {categoryNames[product.category] || product.category}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-foreground">{product.name}</span>
          </nav>

          {/* Product Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square rounded-2xl overflow-hidden bg-muted relative group">
                <img
                  src={allImages[selectedImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
                
                {/* Navigation Arrows (only if multiple images) */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    
                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-background/90 text-sm font-medium">
                      {selectedImageIndex + 1} / {allImages.length}
                    </div>
                  </>
                )}

                {/* Save Button */}
                <button
                  onClick={handleSaveToggle}
                  disabled={saving}
                  className={`absolute top-4 right-4 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isSaved 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-background/90 text-foreground/70 hover:text-primary"
                  }`}
                  data-testid="save-product-btn"
                >
                  <Heart className={`w-6 h-6 ${isSaved ? "fill-current" : ""}`} />
                </button>
              </div>

              {/* Thumbnail Gallery */}
              {allImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index 
                          ? "border-primary" 
                          : "border-transparent hover:border-border"
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} - View ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col">
              {product.is_featured && (
                <span className="badge-preorder self-start px-4 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full text-white mb-4">
                  Featured
                </span>
              )}
              
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground" data-testid="product-name">
                {product.name}
              </h1>
              

              <p className="text-foreground/80 mt-6 leading-relaxed text-lg" data-testid="product-description">
                {product.description}
              </p>

              {/* Product Specifications */}
              {features.length > 0 && (
                <div className="mt-8 p-5 bg-muted/30 rounded-xl border border-border/50">
                  <h3 className="font-heading font-semibold text-foreground mb-4">Specifications</h3>
                  <div className="space-y-3">
                    {features.map((feature) => (
                      <div key={feature.label} className="flex items-start gap-3">
                        <span className="text-sm font-medium text-muted-foreground min-w-[100px]">{feature.label}:</span>
                        <span className="text-foreground">{feature.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* General Features */}
              <div className="mt-8 space-y-3">
                {generalFeatures.map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-secondary" />
                    </div>
                    <span className="text-foreground/70">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Enquiry CTA */}
              <div className="mt-10 p-6 bg-muted/50 rounded-2xl">
                <p className="text-sm text-muted-foreground mb-4">
                  Interested in this piece? Send us an enquiry and we&apos;ll get back to you with details.
                </p>
                <Button
                  size="lg"
                  onClick={() => setIsModalOpen(true)}
                  className="w-full md:w-auto rounded-full px-10 py-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold btn-press shadow-lg shadow-primary/20"
                  data-testid="preorder-btn"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Enquire Now
                </Button>
              </div>

              {/* Category link */}
              <div className="mt-8 pt-6 border-t border-border">
                <span className="text-sm text-muted-foreground">Category: </span>
                <Link 
                  to={`/category/${product.category}`}
                  className="text-sm text-primary font-medium hover:underline"
                >
                  {categoryNames[product.category] || product.category}
                </Link>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-24">
              <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
                <div>
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                    Similar Items
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground mt-2">
                    You May Also Like
                  </h2>
                </div>
                <Link 
                  to={`/category/${product.category}`}
                  className="text-foreground/60 hover:text-primary font-medium flex items-center gap-2 transition-colors text-sm tracking-wide"
                >
                  View All in Category
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((relProduct) => (
                  <ProductCard key={relProduct.id} product={relProduct} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      <PreOrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={product}
      />
    </div>
  );
};

export default ProductPage;
